import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { computed } from 'vue';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

interface TitleSqtvConfig {
	titleId: string;
	providerShortName: string;
	countries: string[];
	// BI-computed targeting list this config is scoped to. When set,
	// all other matching checks (offers/excludeMonetizationTypes/tags/subgenres) are skipped — the title
	// either is in the list or it isn't.
	listName?: string;
	tags?: string[];
	subgenres?: string[];
	excludeMonetizationTypes?: string[];
	// additional providers to exclude
	excludeProviders?: string[];
}

type TitleSqtvTargeting = { provider: string; placement: 'sqtv'; titleId: string };

// Per-title SQTV overrides. First matching entry takes priority.
const TITLE_SQTV_CONFIGS: TitleSqtvConfig[] = [
	{
		titleId: 'tm940647',
		providerShortName: 'jwt',
		countries: ['DE', 'CA'],
		tags: [
			'21st_century',
			'athlete_and_trainer',
			'buddies',
			'championship',
			'comedy',
			'contemporary',
			'contests_and_competitions',
			'friends',
			'friendship',
			'humorous',
		],
		subgenres: ['crfc'],
		excludeMonetizationTypes: [],
	},
	{
		titleId: 'tm1511674',
		providerShortName: 'jwt',
		countries: ['AU'],
		listName: 'INSIDE_2025_AU',
	},
	{
		titleId: 'tm1511674',
		providerShortName: 'jwt',
		countries: ['NZ'],
		listName: 'INSIDE_2025_NZ',
	},
];

// Exported for reuse by other SQTV-style targeting composables (e.g. useSportsSqtvTargeting).
export function titleHasTag(title: TitleDetail, tags: string[]): boolean {
	return (title.content.tags ?? []).some(t => tags.includes(t.technicalName));
}

export function titleHasSubgenre(title: TitleDetail, subgenres: string[]): boolean {
	return (title.content.subgenres ?? []).some(s => subgenres.includes(s.content.shortName ?? ''));
}

export function titleHasGenre(title: TitleDetail, genres: string[]): boolean {
	return (title.content.genres ?? []).some(g => genres.includes(g.shortName ?? ''));
}

export function getTitleSqtvTargeting(
	title: TitleDetail,
	country: string,
	inLists: string[] = []
): TitleSqtvTargeting | null {
	for (const config of TITLE_SQTV_CONFIGS) {
		// Config only applies to specific countries
		if (!config.countries.includes(country)) continue;

		// List-based config: skip all other checks — just match on list membership
		if (config.listName) {
			// temp: manual exclusion of kids and family from Inside 2025 list
			if (config.listName === 'INSIDE_2025_AU' && title.content.genres.some(g => g.shortName === 'fml')) {
				continue;
			}
			if (inLists.includes(config.listName) && title.id !== config.titleId) {
				return { provider: config.providerShortName, placement: 'sqtv', titleId: config.titleId };
			}
			continue;
		}

		// Title already available on the target provider — no need to cross-promote
		if (
			title.offers.some(
				o =>
					o.package.shortName === config.providerShortName ||
					config.excludeProviders?.includes(o.package.shortName ?? '')
			)
		)
			continue;

		// Title has an offer on the target provider with an excluded monetization type (e.g. Free) — skip
		if (config.excludeMonetizationTypes?.length) {
			const excluded = title.offers.some(
				o =>
					o.package.shortName === config.providerShortName &&
					config.excludeMonetizationTypes!.includes(o.monetizationType)
			);
			if (excluded) continue;
		}

		// Title must match at least one of the configured tags or subgenres
		const tagMatch = config.tags?.length ? titleHasTag(title, config.tags) : true;
		const subgenreMatch = config.subgenres?.length ? titleHasSubgenre(title, config.subgenres) : true;
		if (!tagMatch && !subgenreMatch) continue;

		return { provider: config.providerShortName, placement: 'sqtv', titleId: config.titleId };
	}
	return null;
}

// Checks whether any config is active for a given provider+country (used by useSingleQTV to scope jwEntityIDs)
export function isTitleSqtvProviderActive(shortName: string | null, country: string): boolean {
	return TITLE_SQTV_CONFIGS.some(c => c.providerShortName === shortName && c.countries.includes(country));
}

// Returns the pinned titleId for the matching config (used by useSingleQTV)
export function getTitleSqtvTitleId(shortName: string | null, country: string): string | null {
	return (
		TITLE_SQTV_CONFIGS.find(c => c.providerShortName === shortName && c.countries.includes(country))?.titleId ??
		null
	);
}

export function useTitleSqtvTargeting(title: MaybeRefOrGetter<TitleDetail>, enabled: MaybeRefOrGetter<boolean> = true) {
	const { country } = storeToRefs(useLanguageStore());
	const { inLists } = storeToRefs(useTitleDetailsStore());

	const _title = computed(() => toValue(title));
	// const active = computed(() => toValue(enabled) && !!getTitleSqtvTargeting(_title.value, country.value));
	const targetedTitle = computed(() =>
		toValue(enabled) ? getTitleSqtvTargeting(_title.value, country.value, inLists.value) : null
	);

	const isTitleTargetedSqtvActive = computed(() => !!targetedTitle.value);

	return { isTitleTargetedSqtvActive };
}
